import React, { createContext, useState, useContext, useEffect } from 'react';
import { useTonAddress, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const walletAddress = useTonAddress();
  const wallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Monitor wallet connection status
    const handleStatusChange = (walletInfo) => {
      if (walletInfo) {
        console.log('Wallet connected:', walletInfo);
        toast.success('Wallet connected successfully!');
      }
    };

    // Listen for connection changes
    if (tonConnectUI) {
      tonConnectUI.onStatusChange(handleStatusChange);
    }

    return () => {
      if (tonConnectUI) {
        tonConnectUI.onStatusChange(() => {});
      }
    };
  }, [tonConnectUI]);

  useEffect(() => {
    // Update user wallet address when wallet connects
    if (walletAddress && user && user.wallet_address !== walletAddress) {
      updateUserWallet(walletAddress);
    }
  }, [walletAddress, user]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const updateUserWallet = async (address) => {
    try {
      await axios.patch(
        `${API}/auth/update-wallet`,
        { wallet_address: address },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(prev => ({ ...prev, wallet_address: address }));
      toast.success('Wallet linked to your account!');
    } catch (error) {
      console.error('Failed to update wallet:', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      setToken(response.data.token);
      setUser(response.data.user);
      localStorage.setItem('token', response.data.token);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, wallet_address) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        wallet_address: wallet_address || walletAddress
      });
      setToken(response.data.token);
      setUser(response.data.user);
      localStorage.setItem('token', response.data.token);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    if (tonConnectUI && tonConnectUI.connected) {
      tonConnectUI.disconnect();
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, walletAddress, wallet, tonConnectUI }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};